import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

const T = {
  navy: "#A80D2C",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
};

// ── Distinct colors for each of the 6 leadership styles ─────────────────────
const STYLE_COLORS = {
  authoritative: { color: "#1e5fa8", bg: "#f0f6ff", gradient: "linear-gradient(135deg,#1e3a8a,#1e5fa8)" },
  coercer:       { color: "#b91c1c", bg: "#fff5f5", gradient: "linear-gradient(135deg,#7f1d1d,#b91c1c)" },
  democrat:      { color: "#0d7a6e", bg: "#f0fdf9", gradient: "linear-gradient(135deg,#064e3b,#0d7a6e)" },
  affiliator:    { color: "#7c3aed", bg: "#faf5ff", gradient: "linear-gradient(135deg,#4c1d95,#7c3aed)" },
  pacesetter:    { color: "#c2570b", bg: "#fff7ed", gradient: "linear-gradient(135deg,#7c2d12,#c2570b)" },
  coach:         { color: "#0e6ea8", bg: "#f0f9ff", gradient: "linear-gradient(135deg,#0c4a6e,#0e6ea8)" },
};

// ── Static lookup tables ─────────────────────────────────────────────────────
const STYLE_DEFINITIONS = [
  {
    key: "authoritative",
    label: "Authoritative",
    tagline: '"Come with me!"',
    overview:
      "You provide clear, long-term direction and vision. People know exactly where they are going. This is highly effective when a team needs a new vision or clear guidance.",
    execFrame:
      "Focuses on mobilizing people toward a shared vision. This style provides strategic clarity and long-term direction, making it highly effective during periods of organizational transformation, turnarounds, or when establishing a new strategic course.",
    strengths: "Creates clarity, inspires commitment, and aligns effort around purpose.",
    weaknesses: "Can become overly directive if used in situations that require more participation or autonomy.",
    bestUsed: "A new direction is needed, during change, or when a team needs clarity and inspiration.",
  },
  {
    key: "coercer",
    label: "Coercer",
    tagline: '"Do what I tell you!"',
    overview:
      "You demand immediate compliance and obedience. This style is incredibly effective in a crisis or emergency, but if used too often, it can hurt team morale.",
    execFrame:
      "Demands immediate compliance and operates with unilateral decision-making. While over-reliance on this style can degrade organizational climate, it remains a critical asset during crises, genuine emergencies, or when dealing with highly problematic performers.",
    strengths: "Creates immediate control, establishes order, and is effective in urgent situations.",
    weaknesses: "Can damage trust, reduce engagement, and suppress initiative when overused.",
    bestUsed: "Quick action is essential, especially in a crisis or when dealing with repeated non-performance.",
  },
  {
    key: "democrat",
    label: "Democrat",
    tagline: '"What do you think?"',
    overview:
      "You build consensus by getting everyone's input. This is the best style to use when you need buy-in from your team or when you want to generate fresh ideas.",
    execFrame:
      "Leverages collective intelligence by forging consensus through participation. This style is optimal for driving team alignment, generating innovative solutions, and navigating complex matrixed stakeholder environments.",
    strengths: "Encourages participation, improves buy-in, and often leads to better ideas through shared thinking.",
    weaknesses: "Can slow decisions when speed matters or when too many viewpoints create delay.",
    bestUsed: "Consensus is important, or when a leader wants to draw on team expertise and engagement.",
  },
  {
    key: "affiliator",
    label: "Affiliator",
    tagline: '"People come first."',
    overview:
      "You focus on creating harmony and building emotional bonds. This is an excellent style for healing team conflicts or motivating people during stressful times.",
    execFrame: null,
    strengths: "Builds trust, strengthens morale, and helps people feel valued and connected.",
    weaknesses: "Can avoid tough conversations or underperformance if harmony is prioritised over accountability.",
    bestUsed: "A team needs healing, emotional support, or a stronger sense of belonging.",
  },
  {
    key: "pacesetter",
    label: "Pacesetter",
    tagline: '"Do as I do, right now!"',
    overview:
      "You set extremely high standards for performance and lead by example. This works great with highly motivated, competent teams, but can overwhelm others.",
    execFrame: null,
    strengths: "Drives pace, raises performance standards, and creates momentum.",
    weaknesses: "Can create pressure, reduce morale, and increase burnout if used too often or without support.",
    bestUsed: "The team is already skilled and motivated, and performance needs to be raised quickly.",
  },
  {
    key: "coach",
    label: "Coach",
    tagline: '"Try this."',
    overview:
      "You focus heavily on developing people for the future. This style is perfect for helping employees improve their weaknesses and build long-term strengths.",
    execFrame: null,
    strengths: "Develops capability, strengthens future readiness, and supports long-term growth.",
    weaknesses: "Requires time, patience, and strong coaching skill; may be less effective when urgency is high.",
    bestUsed: "The goal is to build future strength, improve performance, or support long-term development.",
  },
];

const DRIVER_DEFINITIONS = [
  {
    key: "power",
    label: "Power",
    desc: "You are energized by the desire to influence others, take charge, and make a strong impact on your organization. At the executive level, this translates to a drive to influence outcomes, architect systemic change, and take decisive charge of organizational direction.",
  },
  {
    key: "affiliation",
    label: "Affiliation",
    desc: "You are energized by building strong, friendly, and close interpersonal relationships with your team. Executives with this driver are motivated by cultivating robust, collaborative networks and fostering a deeply cohesive, supportive team culture.",
  },
  {
    key: "achievement",
    label: "Achievement",
    desc: "You are energized by overcoming challenges, hitting goals, and excelling against high standards. At the executive level, this reflects a continuous pursuit of excellence, overcoming complex business challenges, and consistently exceeding established performance benchmarks.",
  },
];

// ── Radar SVG helper ─────────────────────────────────────────────────────────
function RadarSVG({ stylesData }) {
  const size = 480;
  const cx = size / 2;
  const cy = size / 2;
  const r = 110;
  const levels = 5;
  const maxVal = 10;
  const n = stylesData.length;

  const angleOf = (i) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pointAt = (angle, radius) => ({
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  });

  const gridPolygons = Array.from({ length: levels }, (_, lvl) => {
    const frac = (lvl + 1) / levels;
    return stylesData
      .map((_, i) => {
        const p = pointAt(angleOf(i), r * frac);
        return `${p.x},${p.y}`;
      })
      .join(" ");
  });

  const dataPoints = stylesData
    .map(({ score }, i) => {
      const val = score ?? 0;
      const p = pointAt(angleOf(i), r * (val / maxVal));
      return `${p.x},${p.y}`;
    })
    .join(" ");

  const axes = stylesData.map((_, i) => {
    const end = pointAt(angleOf(i), r);
    return { x1: cx, y1: cy, x2: end.x, y2: end.y };
  });

  const labels = stylesData.map(({ def, score }, i) => {
    const angle = angleOf(i);
    const lp = pointAt(angle, r + 36);
    const anchor = Math.abs(lp.x - cx) < 8 ? "middle" : lp.x < cx ? "end" : "start";
    return { x: lp.x, y: lp.y, label: def.label, score, anchor };
  });

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      style={{ display: "block", margin: "0 auto", overflow: "visible" }}
    >
      {gridPolygons.map((pts, i) => (
        <polygon
          key={i}
          points={pts}
          fill="none"
          stroke="#DCE5EF"
          strokeWidth={i === levels - 1 ? 1.5 : 0.8}
        />
      ))}
      {axes.map((a, i) => (
        <line key={i} x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="#DCE5EF" strokeWidth={1} />
      ))}
      <polygon
        points={dataPoints}
        fill={`${T.navy}30`}
        stroke={T.navy}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {stylesData.map(({ score }, i) => {
        const val = score ?? 0;
        const p = pointAt(angleOf(i), r * (val / maxVal));
        return (
          <circle key={i} cx={p.x} cy={p.y} r={5} fill={T.blue} stroke="#fff" strokeWidth={1.5} />
        );
      })}
      {labels.map(({ x, y, label, score, anchor }, i) => (
        <g key={i}>
          <text
            x={x} y={y - 5}
            textAnchor={anchor}
            fontSize="9"
            fontWeight="700"
            fill={T.slate}
            style={{ textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}
          >
            {label.toUpperCase()}
          </text>
          {score != null && (
            <text
              x={x} y={y + 12}
              textAnchor={anchor}
              fontSize="12"
              fontWeight="700"
              fill={T.navy}
              style={{ fontFamily: "Arial, sans-serif" }}
            >
              {score}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function HiddenEMSReport({ participantData, onComplete }) {
  const containerRef = useRef(null);
  const hasRun = useRef(false);

  // ── Load html2canvas + jsPDF once ─────────────────────────────────────────
  useEffect(() => {
    let pending = 2;
    const done = () => { pending--; };
    const load = (src, id) => {
      if (document.getElementById(id)) { done(); return; }
      const s = document.createElement("script");
      s.id = id; s.src = src;
      s.onload = done; s.onerror = done;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-ems-v2");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", "jspdf-ems-v2");
  }, []);

  // ── Generate PDF whenever participantData is set ──────────────────────────
  useEffect(() => {
    if (!participantData) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const generate = async () => {
      await new Promise((res) => {
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
          setTimeout(check, 100);
        };
        check();
      });

      await new Promise((r) => setTimeout(r, 1400));

      const el = containerRef.current;
      if (!el) { hasRun.current = false; onComplete?.(); return; }

      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".ems-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth,
            height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX = 25.4 / 96;
          const naturalW = logicalW * MM_PER_PX;
          const naturalH = logicalH * MM_PER_PX;
          const scaleToW = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.userInfo?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`EMS_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("EMS PDF generation failed", err);
      } finally {
        hasRun.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]);

  if (!participantData) return null;

  // ── Derive all display values ──────────────────────────────────────────────
  const {
    userInfo = {},
    styleScores = {},
    driverScores = {},
    insightText = "",
    submission_time,
  } = participantData;

  const participant = userInfo.name || "—";
  const assessmentDate = submission_time
    ? formatDate(new Date(submission_time))
    : formatDate(new Date());

  const stylesForDisplay = STYLE_DEFINITIONS.map((def) => ({
    def,
    score: styleScores[def.key] ?? null,
  }));

  const driversForDisplay = DRIVER_DEFINITIONS.map((d) => ({
    def: d,
    score: driverScores[d.key] ?? null,
  }));

  const maxDriver = driverScores
    ? Math.max(...Object.values(driverScores).filter(Number.isFinite), 1)
    : 20;

  const sortedStyles = [...stylesForDisplay].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const dominantStyle = sortedStyles[0]?.def?.label || "—";
  const secondStyle = sortedStyles[1]?.def?.label || "—";

  const sortedDrivers = [...driversForDisplay].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const dominantDriver = sortedDrivers[0]?.def?.label || "—";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      {/* Font — Satoshi, matching VAK report */}
      <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />

      {/* ── All CSS scoped under .ems-pdf-root ── */}
      <style>{`
        .ems-pdf-root {
          font-family: 'Satoshi', sans-serif;
          background: linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
          color: #1D2733;
          line-height: 1.55;
        }
        .ems-pdf-root .report { max-width: 1100px; margin: 0 auto; padding: 2rem 1rem 4rem; }

        /* ── Page shell ── */
        .ems-pdf-root .ems-page {
          background: #ffffff;
          border: 1px solid #d9e0e7;
          border-radius: 28px;
          box-shadow: 0 18px 40px rgba(14,39,68,.10);
          overflow: hidden;
          margin-bottom: 2rem;
        }

        /* ── Cover ── */
        .ems-pdf-root .cover { min-height: 1556px; position: relative; color: white; }
        .ems-pdf-root .cover-inner { padding: 3.5rem; display: flex; flex-direction: column; justify-content: space-between; min-height: 1556px; }
        .ems-pdf-root .brand { display: flex; align-items: center; justify-content: space-between; gap: 1rem; position: relative; z-index: 2; }
        .ems-pdf-root .badge { border: 1px solid rgba(255,255,255,.2); background: rgba(255,255,255,.1); padding: .5rem 1rem; border-radius: 999px; font-size: 0.76rem; }

        /* Logo panel — identical to VAK */
        .ems-pdf-root .logo-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: 500px;
          height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        .ems-pdf-root .logo-panel-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          padding-left: 60px;
          padding-bottom: 25px;
        }
        .ems-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        .ems-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:3.4rem;line-height:.95; color:#FFFFFF;white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .ems-pdf-root .cover-title p  { font-size: 1.2rem; color: rgba(255,255,255,.82); max-width: 100%; text-align: justify; }
        .ems-pdf-root .cover-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1rem; margin-top: 2.5rem; }
        .ems-pdf-root .meta { background: rgba(255,255,255,.1); font-size: 1.2rem; border: 1px solid rgba(255,255,255,.14); border-radius: 16px; padding: .9rem 1rem; }
        .ems-pdf-root .meta span   { display: block; font-size: 0.75rem; color: rgba(255,255,255,.65); margin-bottom: .2rem; }
        .ems-pdf-root .meta strong { font-size: 0.95rem; color: #FFFFFF; }
        .ems-pdf-root .cover-badges { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: 2rem; }
        .ems-pdf-root .cover-badge  { background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.18); padding: .5rem .9rem; border-radius: 999px; font-size: 0.95rem; }
        .ems-pdf-root .footer-note { display: flex; justify-content: space-between; gap: 1rem; color: rgba(255,255,255,.68); font-size: 0.75rem; margin-top: 1rem; }

        /* ── Inner pages ── */
        .ems-pdf-root .page-inner { padding: 2.5rem; }
        .ems-pdf-root .topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 2rem; }
        .ems-pdf-root .section-kicker { color: #c62828; font-size: 0.8rem; text-transform: uppercase; letter-spacing: .12em; font-weight: 700; }
        .ems-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }

        /* ── Cards ── */
        .ems-pdf-root .card { background: linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border: 1px solid #d9e0e7; border-radius: 20px; padding: 1.5rem; }
        .ems-pdf-root .card h3 { margin: 0 0 .75rem; font-size: 1.2rem; color: #0f2744; }
        .ems-pdf-root .lead { color: #334155; font-size: 1rem; margin: 0; text-align: justify; }
        .ems-pdf-root .pill { display: inline-flex; align-items: center; gap: .45rem; padding: .5rem .85rem; border-radius: 999px; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; }
        .ems-pdf-root .pill.red { background: #fef2f2; color: #c62828; }

        /* ── Inner sub-panel ── */
        .ems-pdf-root .sub-panel { background: linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border: 1px solid #d9e0e7; border-radius: 20px; padding: 1.25rem; }
        .ems-pdf-root .sub-panel h4 { margin: 0 0 .6rem; font-size: 1rem; color: #0f2744; }

        /* ── Bars ── */
        .ems-pdf-root .bar-row { display: grid; grid-template-columns: 160px 1fr 42px; gap: 10px; align-items: center; margin-bottom: .8rem; }
        .ems-pdf-root .bar-label { font-size: 0.9rem; font-weight: 700; color: #0f2744; }
        .ems-pdf-root .track { height: 20px; background: #e9eef3; border-radius: 999px; overflow: hidden; }
        .ems-pdf-root .fill  { height: 100%; border-radius: 999px; }
        .ems-pdf-root .score-val { text-align: right; font-size: 0.9rem; font-weight: 700; color: #0f2744; }

        /* ── Driver tiles ── */
        .ems-pdf-root .driver-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: .8rem; margin-bottom: .85rem; }
        .ems-pdf-root .driver-tile { border-radius: 14px; border: 1px solid #d9e0e7; padding: .85rem; background: linear-gradient(180deg,#fff,#f8fbff); }
        .ems-pdf-root .driver-tile span   { display: block; font-size: 0.72rem; letter-spacing: .08em; text-transform: uppercase; color: #5B677A; font-weight: 700; margin-bottom: .2rem; }
        .ems-pdf-root .driver-tile strong { font-size: 1.85rem; color: #A80D2C; font-weight: 700; display: block; }

        /* ── Callout ── */
        .ems-pdf-root .callout { background: linear-gradient(135deg,#fff7f7,#fbeaea); border: 1px solid #d9e0e7; border-radius: 18px; padding: 1.25rem 1.5rem; color: #334155; text-align: justify; }

        /* ── Style item (Page 3) — VAK card style with per-style color accent ── */
        .ems-pdf-root .style-item { border: 1px solid #d9e0e7; border-radius: 16px; padding: 1.1rem; margin-bottom: .85rem; background: #fff; }
        .ems-pdf-root .style-item-header { display: flex; align-items: center; gap: .85rem; margin-bottom: .6rem; }
        .ems-pdf-root .style-score-badge { width: 48px; height: 48px; border-radius: 12px; display: grid; place-items: center; font-size: 1.35rem; font-weight: 800; color: white; flex-shrink: 0; }
        .ems-pdf-root .style-item-meta strong { display: block; font-size: .97rem; color: #0f2744; }
        .ems-pdf-root .style-item-meta em     { font-style: italic; font-size: .82rem; color: #5B677A; }
        .ems-pdf-root .style-item p { margin: 0 0 .35rem; font-size: 0.88rem; color: #334155; text-align: justify; }
        .ems-pdf-root .style-item .exec { color: #5B677A; font-size: 0.82rem; text-align: justify; }

        /* ── Two-col ── */
        .ems-pdf-root .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.2rem; }

        /* ── Driver cards (Page 4) ── */
        .ems-pdf-root .driver-card { background: linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border: 1px solid #d9e0e7; border-radius: 20px; padding: 1.25rem; }
        .ems-pdf-root .driver-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: .6rem; }
        .ems-pdf-root .driver-card-header h3 { margin: 0; font-size: 1.1rem; color: #0f2744; }
        .ems-pdf-root .driver-big-score { font-size: 2rem; font-weight: 800; color: #A80D2C; }
        .ems-pdf-root .driver-card p  { margin: 0; font-size: 0.88rem; color: #334155; line-height: 1.6; text-align: justify; }

        /* ── Back cover ── */
        .ems-pdf-root .back-cover { min-height: 1556px; color: white; }
        .ems-pdf-root .back-cover .page-inner { min-height: 1556px; display: flex; flex-direction: column; justify-content: space-between; }
        .ems-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .ems-pdf-root .closing p  { text-align: justify; max-width: 100%; color: rgba(255,255,255,.82); font-size: 1.2rem; }
        .ems-pdf-root .contact-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1rem; margin-top: 1.75rem; }
        .ems-pdf-root .contact { background: rgba(193,24,24,.1); border: 1px solid rgba(255,255,255,.16); border-radius: 16px; padding: .9rem; }
        .ems-pdf-root .contact span   { display: block; color: rgba(255,255,255,.64); font-size: 0.73rem; margin-bottom: .25rem; }
        .ems-pdf-root .contact strong { font-size: 0.92rem; }
        .ems-pdf-root .small { font-size: 0.73rem; color: #5B677A; }
        .ems-pdf-root .light { color: rgba(255,255,255,.66); }

        /* ── Driver bar ── */
        .ems-pdf-root .driver-bar-row { display: grid; grid-template-columns: 130px 1fr 38px; gap: 10px; align-items: center; margin-bottom: .7rem; }
      `}</style>

      <div className="ems-pdf-root">
        <main className="report">

          {/* ══════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════ */}
          <section className="ems-page cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
                minHeight: "1556px",
                flexDirection: "column",
              }}
            >
              {/* Logo panel — identical pattern to VAK */}
              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>

              <div className="cover-inner">
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: "relative", zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>Leadership Styles Assessment</h2>
                    <p>
                      A comprehensive leadership profile report covering six management
                      styles and three motivational drivers, presented in a
                      consulting-ready visual format to support targeted development
                      and behavioural growth.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta">
                      <span>Participant</span>
                      <strong>{participant}</strong>
                    </div>
                    <div className="meta">
                      <span>Assessment Date</span>
                      <strong>{assessmentDate}</strong>
                    </div>
                    <div className="meta">
                      <span>Report Type</span>
                      <strong>Individual Leadership Profile</strong>
                    </div>
                  </div>

                  <div className="cover-badges">
                    {["Executive Dashboard", "Six Leadership Styles", "Three Motivational Drivers"].map((b) => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note" style={{ position: "relative", zIndex: 2 }}>
                  <div>Based on Goleman's six leadership styles framework and three motivational drivers.</div>
                  <div>Each style scored out of 10.</div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 2 — EXECUTIVE DASHBOARD
          ══════════════════════════════════════════ */}
          <section className="ems-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Executive Dashboard</h2>
                </div>
                <div className="pill red">Final adjusted scores</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                This section presents final adjusted scores across the six leadership
                styles and three motivational drivers. Scores reflect dominant and
                secondary management behaviours, each out of a maximum of 10 points.
              </p>

              {/* Two columns: radar left, drivers right */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", marginBottom: "1.25rem" }}>

                {/* Left — Radar chart */}
                <div className="sub-panel">
                  <h4>Your Leadership Styles Breakdown</h4>
                  <RadarSVG stylesData={stylesForDisplay} />
                </div>

                {/* Right — Drivers + insight */}
                <div className="sub-panel">
                  <h4>Your Core Motivational Drivers</h4>
                  <p style={{ color: T.slate, fontSize: ".88rem", margin: "0 0 .85rem", textAlign: "justify" }}>
                    These drivers represent the macro-level themes that energize your management approach.
                  </p>
                  <div className="driver-grid">
                    {driversForDisplay.map(({ def, score }) => (
                      <div className="driver-tile" key={def.key}>
                        <span>{def.label}</span>
                        <strong>{score ?? "—"}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="callout">
                    <strong>Evaluation insight: </strong>
                    {insightText || "Leadership effectiveness grows when styles are used flexibly in response to team and situational needs."}
                  </div>
                </div>
              </div>

              {/* Style bars at a glance */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
                <div className="card">
                  <h3>Style Scores at a Glance <span style={{ fontSize: ".78rem", color: T.slate, fontWeight: 400 }}>— out of 10</span></h3>
                  {stylesForDisplay.map(({ def, score }) => {
                    const sc = STYLE_COLORS[def.key];
                    return (
                      <div className="bar-row" key={def.key}>
                        <div className="bar-label" style={{ color: sc.color }}>{def.label}</div>
                        <div className="track">
                          <div
                            className="fill"
                            style={{ width: `${((score ?? 0) / 10) * 100}%`, background: sc.gradient }}
                          />
                        </div>
                        <div className="score-val" style={{ color: sc.color }}>{score ?? "—"}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="card">
                  <h3>Motivational Driver Spread <span style={{ fontSize: ".78rem", color: T.slate, fontWeight: 400 }}>— raw scores</span></h3>
                  {driversForDisplay.map(({ def, score }) => (
                    <div className="driver-bar-row" key={def.key}>
                      <div className="bar-label">{def.label}</div>
                      <div className="track">
                        <div
                          className="fill"
                          style={{
                            width: `${((score ?? 0) / maxDriver) * 100}%`,
                            background: `linear-gradient(90deg,${T.navy},${T.blue},${T.gold})`,
                          }}
                        />
                      </div>
                      <div className="score-val">{score ?? "—"}</div>
                    </div>
                  ))}

                  <div style={{ marginTop: "1rem", padding: ".85rem", background: "#f8f4eb", borderRadius: "14px", border: `1px solid ${T.gold}44` }}>
                    <div style={{ fontSize: ".75rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".3rem" }}>
                      Dominant Profile
                    </div>
                    <div style={{ fontSize: ".9rem", color: T.ink }}>
                      Style: <strong style={{ color: T.navy }}>{dominantStyle}</strong>
                      {" "}(secondary: <strong>{secondStyle}</strong>)
                    </div>
                    <div style={{ fontSize: ".9rem", color: T.ink, marginTop: ".25rem" }}>
                      Driver: <strong style={{ color: T.navy }}>{dominantDriver}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 3 — SIX LEADERSHIP STYLES
          ══════════════════════════════════════════ */}
          <section className="ems-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">The Six Leadership Styles</h2>
                </div>
                <div className="pill red">Scored out of 10 per style</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                Effective leaders adapt their approach based on the situation, the
                people involved, and the outcomes they want to achieve. The six styles
                below represent your full leadership repertoire.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.1rem" }}>

                {/* Full-width — first 3 styles, dominant first, full detail */}
                <div>
                  {[...stylesForDisplay]
                    .sort((a, b) => {
                      const aDom = a.def.label === dominantStyle;
                      const bDom = b.def.label === dominantStyle;
                      return aDom === bDom ? 0 : aDom ? -1 : 1;
                    })
                    .slice(0, 3)
                    .map(({ def, score }) => {
                    const sc = STYLE_COLORS[def.key];
                    const isDominant = def.label === dominantStyle;
                    return (
                      <div
                        key={def.key}
                        className="style-item"
                        style={{
                          borderTop:    isDominant ? `1.5px solid ${sc.color}` : `1px solid #d9e0e7`,
                          borderRight:  isDominant ? `1.5px solid ${sc.color}` : `1px solid #d9e0e7`,
                          borderBottom: isDominant ? `1.5px solid ${sc.color}` : `1px solid #d9e0e7`,
                          borderLeft:   `5px solid ${sc.color}`,
                          background:   `linear-gradient(135deg, ${sc.bg} 0%, #ffffff 60%)`,
                        }}
                      >
                        <div className="style-item-header">
                          <div className="style-score-badge" style={{ background: sc.gradient }}>
                            {score ?? "—"}
                          </div>
                          <div className="style-item-meta">
                            <strong style={{ color: sc.color }}>{def.label}</strong>
                            <em>{def.tagline}</em>
                          </div>
                          {isDominant && (
                            <div style={{ background: sc.color, color: "#fff", fontSize: "10px", fontWeight: 800, padding: "3px 10px", borderRadius: "999px", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap", marginLeft: "auto" }}>
                              ★ Primary Style
                            </div>
                          )}
                        </div>
                        <p>{def.overview}</p>
                        {def.execFrame && <p className="exec">{def.execFrame}</p>}
                        <p><strong>Strengths:</strong> {def.strengths}</p>
                        <p><strong>Weaknesses:</strong> {def.weaknesses}</p>
                        <p><strong>Best used when:</strong> {def.bestUsed}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 4 — SIX LEADERSHIP STYLES (CONT'D)
          ══════════════════════════════════════════ */}
          <section className="ems-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">The Six Leadership Styles (cont'd)</h2>
                </div>
                <div className="pill red">Scored out of 10 per style</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.1rem" }}>

                {/* Full-width — remaining 3 styles, full detail */}
                <div>
                  {[...stylesForDisplay]
                    .sort((a, b) => {
                      const aDom = a.def.label === dominantStyle;
                      const bDom = b.def.label === dominantStyle;
                      return aDom === bDom ? 0 : aDom ? -1 : 1;
                    })
                    .slice(3, 6)
                    .map(({ def, score }) => {
                    const sc = STYLE_COLORS[def.key];
                    const isDominant = def.label === dominantStyle;
                    return (
                      <div
                        key={def.key}
                        className="style-item"
                        style={{
                          borderTop:    isDominant ? `1.5px solid ${sc.color}` : `1px solid #d9e0e7`,
                          borderRight:  isDominant ? `1.5px solid ${sc.color}` : `1px solid #d9e0e7`,
                          borderBottom: isDominant ? `1.5px solid ${sc.color}` : `1px solid #d9e0e7`,
                          borderLeft:   `5px solid ${sc.color}`,
                          background:   `linear-gradient(135deg, ${sc.bg} 0%, #ffffff 60%)`,
                        }}
                      >
                        <div className="style-item-header">
                          <div className="style-score-badge" style={{ background: sc.gradient }}>
                            {score ?? "—"}
                          </div>
                          <div className="style-item-meta">
                            <strong style={{ color: sc.color }}>{def.label}</strong>
                            <em>{def.tagline}</em>
                          </div>
                          {isDominant && (
                            <div style={{ background: sc.color, color: "#fff", fontSize: "10px", fontWeight: 800, padding: "3px 10px", borderRadius: "999px", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap", marginLeft: "auto" }}>
                              ★ Primary Style
                            </div>
                          )}
                        </div>
                        <p>{def.overview}</p>
                        {def.execFrame && <p className="exec">{def.execFrame}</p>}
                        <p><strong>Strengths:</strong> {def.strengths}</p>
                        <p><strong>Weaknesses:</strong> {def.weaknesses}</p>
                        <p><strong>Best used when:</strong> {def.bestUsed}</p>
                      </div>
                    );
                  })}

                  {/* Context box */}
                  <div style={{ background: "#f8f4eb", border: `1px solid ${T.gold}55`, borderRadius: "16px", padding: "1rem", marginTop: ".75rem" }}>
                    <div style={{ fontSize: ".72rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".5rem" }}>
                      Context &amp; Meaning
                    </div>
                    <p style={{ margin: 0, fontSize: ".88rem", color: T.ink, lineHeight: 1.6, textAlign: "justify" }}>
                      Rather than being fixed traits, these styles are most effective
                      when used situationally, allowing leaders to respond flexibly to
                      changing contexts, team maturity, and organizational climate.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 5 — MOTIVATIONAL DRIVERS & INTEGRATION
          ══════════════════════════════════════════ */}
          <section className="ems-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">Motivational Drivers &amp; Integration</h2>
                </div>
                <div className="pill red">Power · Affiliation · Achievement</div>
              </div>

              {/* Driver cards — 3 col */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1rem", marginBottom: "1.25rem" }}>
                {driversForDisplay.map(({ def, score }) => (
                  <div className="driver-card" key={def.key}>
                    <div className="driver-card-header">
                      <h3>{def.label}</h3>
                      <span className="driver-big-score">{score ?? "—"}</span>
                    </div>
                    <p>{def.desc}</p>
                  </div>
                ))}
              </div>

              {/* How styles work together */}
              <div className="card" style={{ marginBottom: "1.1rem" }}>
                <h3>How the Styles Work Together</h3>
                <p className="lead">
                  The strongest leaders do not rely on only one style. They shift
                  between styles depending on the challenge, the maturity of the team,
                  and the emotional climate. The framework is best used as a development
                  lens that helps leaders reflect on their dominant tendencies while
                  building flexibility across all six styles.
                </p>
                <div className="callout" style={{ marginTop: ".85rem" }}>
                  <strong>Evaluation insight: </strong>
                  {insightText || "Flexibility across styles — informed by strong self-awareness — is the hallmark of sustained executive effectiveness."}
                </div>
              </div>

              {/* Two-col: understanding scores + coaching note */}
              <div className="two-col">
                <div className="card">
                  <h3>Understanding Your Scores</h3>
                  <p className="lead">
                    Daniel Goleman's research on emotional intelligence shows that
                    leadership is not only about direction and control, but also about
                    climate, relationships, and performance. The six styles work as a
                    repertoire that leaders can use flexibly — some build engagement and
                    trust, while others create urgency and discipline when the situation
                    demands it.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    This report is most powerful when reviewed in a guided debrief with
                    a manager, coach, or facilitator. The goal is not to determine where
                    you stand today, but to identify which behaviours to strengthen and
                    which combinations to deploy more deliberately in your leadership
                    practice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════ */}
          <section className="ems-page back-cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
                minHeight: "1556px",
              }}
            >
              {/* Header bar */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Building Flexible, Adaptive Leadership</h2>
                    <p>
                      Leadership style is not fixed. It is a repertoire that expands
                      through self-awareness, deliberate practice, and consistent
                      reflection. Acumen Business Catalyst uses assessments like this
                      to turn behavioural data into focused development conversations
                      that drive lasting change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Why Style Flexibility Matters</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
                      Research on executive effectiveness consistently shows that leaders
                      who consciously shift between styles — depending on team readiness,
                      task complexity, and organizational context — produce stronger
                      outcomes and healthier cultures than those who rely on a single
                      dominant approach.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact">
                      <span>Brand</span>
                      <strong>Acumen Business Catalyst</strong>
                    </div>
                    <div className="contact">
                      <span>Focus</span>
                      <strong>Leadership • Culture • Capability</strong>
                    </div>
                    <div className="contact">
                      <span>Report type</span>
                      <strong>Individual Leadership Profile</strong>
                    </div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Executive Management Styles (EMS)
                    assessment framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}